/*
 * <advertorial-quiz>
 * ------------------------------------------------------------------
 * Three taps — size, then two qualifying questions — ending in a named set, a
 * price and a link that lands on a variant that is actually in stock.
 *
 * Everything the script needs is rendered by Liquid from the live product:
 * the size buttons carry their own price, formatted price and variant URL as
 * data attributes, so there is no product data duplicated in JavaScript and
 * nothing to re-export when a price or a colour's stock changes. The source
 * design shipped 84 variant ids and seven prices frozen at a date; this reads
 * them at render time instead.
 *
 * Accessibility
 *   Each question is a `group` labelled by its own heading, and each option is
 *   a real <button> reporting `aria-pressed`. Later questions are genuinely
 *   `hidden` rather than visually hidden, so they are out of the tab order
 *   until they apply. The result panel is a polite live region: it appears in
 *   response to the visitor's own tap, and announcing it is the only way a
 *   screen reader user learns anything happened.
 *
 * Progressive enhancement
 *   With JavaScript off the quiz is not useful, so Liquid renders it inside a
 *   <noscript>-friendly shell: the panel keeps its heading and a plain link to
 *   the product page, and the steps stay hidden.
 */
class AdvertorialQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-quiz-step]')];
    this.result = this.querySelector('[data-quiz-result]');
    if (!this.steps.length || !this.result) return;

    this.answers = {};
    this.fallbackUrl = this.dataset.productUrl || '#';

    this.el = {
      match: this.querySelector('[data-quiz-match]'),
      why: this.querySelector('[data-quiz-why]'),
      price: this.querySelector('[data-quiz-price]'),
      claim: this.querySelector('[data-quiz-claim]'),
      // The label is a span inside the link so the decorative arrow beside it
      // survives every rewrite.
      claimLabel: this.querySelector('[data-quiz-claim-label]'),
      reset: this.querySelector('[data-quiz-reset]'),
    };
    this.claimTemplate = this.el.claim?.dataset.labelTemplate || '[size]';
    this.matchTemplate = this.el.match?.dataset.labelTemplate || '[size]';

    // The order the chosen answers' sentences read in. Question order by
    // default; the block can name its own, because which reason leads is a copy
    // decision rather than a structural one. On the fitted-sheet page the fit
    // sentence opens and the cooling one follows it with "And ...", even though
    // the questions are asked the other way round. Anything the list does not
    // mention keeps its place at the end, so a half-filled setting still reads.
    const stepKeys = this.steps.map((el) => el.dataset.quizKey).filter(Boolean);
    const named = (this.dataset.whyOrder || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => (/^\d+$/.test(part) ? `q${part}` : part))
      .filter((key) => stepKeys.includes(key));
    this.whyKeys = [...named, ...stepKeys.filter((key) => !named.includes(key))];

    // Steps beyond the first only become reachable once the one before is
    // answered. Enforced here rather than in Liquid so the markup stays a
    // plain, complete list of questions.
    this.showUpTo(1);

    this.addEventListener('click', (ev) => {
      const button = ev.target.closest('[data-quiz-option]');
      if (!button || !this.contains(button)) return;
      this.choose(button);
    });

    this.el.reset?.addEventListener('click', () => this.reset());
  }

  showUpTo(step) {
    for (const el of this.steps) {
      el.hidden = Number(el.dataset.quizStep) > step;
    }
  }

  choose(button) {
    const key = button.dataset.quizOption;
    this.answers[key] = { ...button.dataset };

    for (const sibling of button.parentNode.querySelectorAll('[data-quiz-option]')) {
      sibling.setAttribute('aria-pressed', String(sibling === button));
    }

    const step = Number(button.closest('[data-quiz-step]').dataset.quizStep);
    if (step < this.steps.length) {
      this.showUpTo(step + 1);
      return;
    }
    this.finish();
  }

  finish() {
    const size = this.answers.size;
    if (!size) return;

    if (this.el.match) {
      this.el.match.textContent = this.matchTemplate.replace('[size]', size.label || '');
    }

    // The "why" is stitched from the sentence each chosen answer carries, so the
    // copy stays entirely in the theme editor. See `whyKeys` for the order.
    if (this.el.why) {
      this.el.why.textContent = this.whyKeys
        .map((key) => this.answers[key]?.why)
        .filter(Boolean)
        .join(' ');
    }

    if (this.el.price) this.el.price.textContent = size.priceFormatted || '';

    if (this.el.claim) {
      const label = this.claimTemplate.replace('[size]', size.label || '');
      (this.el.claimLabel || this.el.claim).textContent = label;
      this.el.claim.href = size.url || this.fallbackUrl;
    }

    this.result.hidden = false;

    // On a phone the answer otherwise renders below the fold, behind the sticky
    // bar, which reads as "nothing happened".
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.result.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });

    this.dispatchEvent(
      new CustomEvent('advertorial:quiz-result', {
        bubbles: true,
        detail: { size: size.label, price: size.priceFormatted, url: size.url },
      })
    );
  }

  reset() {
    this.answers = {};
    this.result.hidden = true;
    for (const b of this.querySelectorAll('[data-quiz-option]')) {
      b.setAttribute('aria-pressed', 'false');
    }
    if (this.el.claim) {
      const label = this.claimTemplate.replace('[size]', '').replace(/\s+/g, ' ').trim();
      (this.el.claimLabel || this.el.claim).textContent = label;
      this.el.claim.href = this.fallbackUrl;
    }
    this.showUpTo(1);
    this.dispatchEvent(new CustomEvent('advertorial:quiz-reset', { bubbles: true }));
    this.querySelector('[data-quiz-option]')?.focus();
  }
}

if (!customElements.get('advertorial-quiz')) {
  customElements.define('advertorial-quiz', AdvertorialQuiz);
}
